/**
 * Crypto Service - AES-256-CBC ile request body şifreleme
 * 
 * Client tarafında body encrypt edilir, server tarafında decrypt edilir.
 * Shared secret .env'den alınır (REACT_APP_ENCRYPTION_KEY).
 */

const ENCRYPTION_KEY = process.env.REACT_APP_ENCRYPTION_KEY || 'c4ll-c3nt3r-s3cr3t-k3y-2026!@#$';

// String'i Uint8Array'e çevir
function stringToBytes(str: string): Uint8Array {
  const encoder = new TextEncoder();
  return encoder.encode(str);
}

// Uint8Array'i hex string'e çevir
function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
}

// Hex string'i Uint8Array'e çevir
function hexToBytes(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.substr(i, 2), 16);
  }
  return bytes;
}

// SHA-256 hash ile 32 byte key oluştur
async function deriveKey(secret: string): Promise<CryptoKey> {
  const keyMaterial = stringToBytes(secret);
  const hash = await crypto.subtle.digest('SHA-256', keyMaterial);
  return crypto.subtle.importKey(
    'raw',
    hash,
    { name: 'AES-CBC' },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * Request body'yi AES-256-CBC ile şifrele
 * @returns { iv: string, data: string } - hex encoded iv ve encrypted data
 */
export async function encryptBody(body: any): Promise<{ iv: string; data: string }> {
  const key = await deriveKey(ENCRYPTION_KEY);
  const iv = crypto.getRandomValues(new Uint8Array(16));
  const plaintext = stringToBytes(JSON.stringify(body));
  
  const encrypted = await crypto.subtle.encrypt(
    { name: 'AES-CBC', iv },
    key,
    plaintext
  );
  
  return {
    iv: bytesToHex(iv),
    data: bytesToHex(new Uint8Array(encrypted))
  };
}

/**
 * Şifrelenmiş veriyi çöz (test amaçlı - normalde server tarafında yapılır)
 */
export async function decryptBody(iv: string, data: string): Promise<any> {
  const key = await deriveKey(ENCRYPTION_KEY);
  const ivBytes = hexToBytes(iv);
  const encryptedBytes = hexToBytes(data);
  
  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-CBC', iv: ivBytes },
    key,
    encryptedBytes
  );
  
  const decoder = new TextDecoder();
  return JSON.parse(decoder.decode(decrypted));
}

export default {
  encryptBody,
  decryptBody
};
